import { UserProfile } from '../userProfile';

export type ChatAudience = 'anonymous' | 'user' | 'partner' | 'admin';

export interface ChatWelcomeContent {
  headline: string;
  message: string;
  subtitle: string;
  quickQuestions: readonly string[];
}

const ANONYMOUS_CONTENT: ChatWelcomeContent = {
  headline: 'Como posso ajudar?',
  message:
    'Sou a Inteligência Wosi. Posso orientar sobre produtos, contratação e navegação no site.',
  subtitle: 'Escolha uma sugestão ou descreva o que precisa.',
  quickQuestions: [
    'Como faço login na minha conta?',
    'Como contratar Mobilidade Elétrica?',
    'Como contratar Painéis Solares?',
    'Quais seguros a Wosi oferece?',
  ],
};

const USER_CONTENT: ChatWelcomeContent = {
  headline: 'Sua conta, em linguagem simples',
  message:
    'Consulto seguros, parcelas e apólice com dados reais da sua conta — sem formulários extras.',
  subtitle: 'Escolha uma sugestão ou conte o que precisa resolver agora.',
  quickQuestions: [
    'Onde vejo meus seguros?',
    'Como consulto minhas parcelas?',
    'Como baixo minha apólice?',
    'Como aviso um sinistro?',
  ],
};

const PARTNER_CONTENT: ChatWelcomeContent = {
  headline: 'Operação do parceiro',
  message:
    'Apoio vendas, clientes, comissões e a gestão da sua operação com dados reais do painel.',
  subtitle: 'Use uma sugestão ou descreva o que precisa fazer.',
  quickQuestions: [
    'Onde vejo minhas vendas e clientes?',
    'Como consulto minhas comissões?',
    'Como cadastro lojas e vendedores?',
    'Como compartilho meu link de vendas?',
  ],
};

const ADMIN_CONTENT: ChatWelcomeContent = {
  headline: 'Operação do dia',
  message:
    'Priorizo o que esta tela pede — com dados reais da Wosi.',
  subtitle: 'Escolha um recorte desta tela ou descreva a ação.',
  quickQuestions: [
    'Quais pendências exigem minha atenção agora?',
    'Quais clientes estão com documentação pendente?',
    'Tem fatura vencida ou cobrança em atraso hoje?',
    'O que posso resolver nesta tela agora?',
  ],
};

const CONTENT_BY_AUDIENCE: Record<ChatAudience, ChatWelcomeContent> = {
  anonymous: ANONYMOUS_CONTENT,
  user: USER_CONTENT,
  partner: PARTNER_CONTENT,
  admin: ADMIN_CONTENT,
};

const PAGE_SETS: { test: RegExp; questions: readonly string[] }[] = [
  {
    test: /agentes/,
    questions: [
      'Quais agentes desta lista estão ativos?',
      'Qual o propósito de cada agente aqui?',
      'Algum playbook desta tela precisa de ajuste?',
      'Como aciono um destes agentes no chat?',
    ],
  },
  {
    test: /automacoes/,
    questions: [
      'Quais automações estão ativas agora?',
      'Alguma execução falhou hoje?',
      'O que a próxima automação vai fazer?',
      'Como crio uma automação para esta rotina?',
    ],
  },
  {
    test: /conhecimento/,
    questions: [
      'Quais regras ativas a IA está usando?',
      'Resuma o que já está nesta base',
      'Alguma regra desta tela ficou desatualizada?',
      'O que ainda falta ensinar à IA?',
    ],
  },
  {
    test: /guardrails/,
    questions: [
      'Quais guardrails estão ativos por audiência?',
      'Alguma política bloqueia a web para o cliente?',
      'Resuma as regras duras desta tela',
      'Preciso ajustar um guardrail agora?',
    ],
  },
  {
    test: /clientes/,
    questions: [
      'Quais clientes desta lista precisam de ação?',
      'Quem está com documentação pendente?',
      'Liste quem está em análise há mais de 3 dias',
      'Tem fatura vencida nestes clientes?',
    ],
  },
  {
    test: /sinistro|aviso-sinistro/,
    questions: [
      'Qual o status deste sinistro?',
      'O que falta para avançar este atendimento?',
      'Mostre a timeline deste sinistro',
      'Quais documentos ainda estão pendentes?',
    ],
  },
  {
    test: /parceiros/,
    questions: [
      'Quais parceiros desta lista estão ativos?',
      'Quem mais vendeu neste mês?',
      'Algum parceiro tem cadastro incompleto?',
      'Quais têm comissão personalizada?',
    ],
  },
  {
    test: /cupom|voucher/,
    questions: [
      'Quais cupons desta lista ainda estão válidos?',
      'Quais vencem nesta semana?',
      'Algum cupom está inativo por engano?',
      'Quem mais usou cupom neste mês?',
    ],
  },
  {
    test: /produtos|solicitacao/,
    questions: [
      'Quais produtos desta lista estão ativos?',
      'Tem solicitação de produto parada?',
      'Algum preço desta tela está desatualizado?',
      'Resuma as pendências de produto',
    ],
  },
  {
    test: /auditoria/,
    questions: [
      'Quais eventos recentes desta auditoria?',
      'Filtre as alterações de hoje',
      'Quem alterou o quê nesta tela?',
      'Tem algum evento crítico recente?',
    ],
  },
  {
    test: /dashboard|conta-home/,
    questions: [
      'Quais pendências exigem atenção agora?',
      'Tem fatura vencida hoje?',
      'Quais sinistros abertos precisam de ação?',
      'Resuma o que mudou hoje no painel',
    ],
  },
];

export function resolveChatAudience(
  isAuthenticated: boolean,
  user: UserProfile | null | undefined
): ChatAudience {
  if (!isAuthenticated || !user) {
    return 'anonymous';
  }

  if (user.hierarchy === 'admin') {
    return 'admin';
  }

  if (user.hierarchy === 'partner') {
    return 'partner';
  }

  return 'user';
}

export function questionsForPageScreen(
  screen: string | null | undefined,
  fallback: readonly string[]
): readonly string[] {
  const key = String(screen ?? '').toLowerCase();
  for (const entry of PAGE_SETS) {
    if (entry.test.test(key)) {
      return entry.questions;
    }
  }
  return fallback.slice(0, 4);
}

export function getChatWelcomeContent(
  isAuthenticated: boolean,
  user: UserProfile | null | undefined,
  screen?: string | null
): ChatWelcomeContent {
  const audience = resolveChatAudience(isAuthenticated, user);
  const base = CONTENT_BY_AUDIENCE[audience];
  return {
    ...base,
    quickQuestions: questionsForPageScreen(screen, base.quickQuestions),
  };
}
